// ============================================
// SERVICE: SINTOMAS
// ============================================
// Funções para gerenciar sintomas via API
// ============================================

import { API_CONFIG } from './staticDataConfig';

const baseURL = API_CONFIG.BASE_URL;

export interface Sintoma {
  id: number;
  valor: string;
  descricao?: string;
  categoria?: string;
  tipo_avaliacao?: 'intensidade' | 'frequencia' | 'ambos';
  ativo: boolean;
  ordem: number;
}

class SintomasService {
  private getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  private getHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Buscar todos os sintomas (ativos ou incluindo inativos)
  async obterTodos(incluirInativos: boolean = false): Promise<Sintoma[]> {
    try {
      const url = incluirInativos 
        ? `${baseURL}/sintomas?incluirInativos=true`
        : `${baseURL}/sintomas`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erro ao buscar sintomas: ${response.statusText}`);
      }

      const data = await response.json();
      // console.log(`[SintomasService] Sintomas obtidos: ${data.sintomas?.length || 0} (incluirInativos: ${incluirInativos})`);
      return data.sintomas || [];
    } catch (error) {
      console.error('[SintomasService] Erro ao obter sintomas:', error);
      throw error;
    }
  }

  // Adicionar novo sintoma
  async adicionar(sintoma: {
    valor: string;
    descricao?: string;
    categoria?: string;
    tipo_avaliacao?: 'intensidade' | 'frequencia' | 'ambos';
    ordem?: number;
  }): Promise<Sintoma> {
    try {
      const response = await fetch(`${baseURL}/sintomas`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(sintoma)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao adicionar sintoma');
      }

      const data = await response.json();
      return data.sintoma;
    } catch (error) {
      console.error('[SintomasService] Erro ao adicionar sintoma:', error);
      throw error;
    }
  }

  // Atualizar sintoma existente
  async atualizar(id: number, sintoma: {
    valor?: string;
    descricao?: string;
    categoria?: string;
    tipo_avaliacao?: 'intensidade' | 'frequencia' | 'ambos';
    ordem?: number;
    ativo?: boolean;
  }): Promise<Sintoma> {
    try {
      const response = await fetch(`${baseURL}/sintomas/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(sintoma)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao atualizar sintoma');
      }

      const data = await response.json();
      return data.sintoma;
    } catch (error) {
      console.error('[SintomasService] Erro ao atualizar sintoma:', error);
      throw error;
    }
  }

  // Excluir sintoma (proteção inteligente)
  async excluir(id: number, forcarDelete: boolean = false): Promise<any> {
    try {
      const url = forcarDelete 
        ? `${baseURL}/sintomas/${id}?forcarDelete=true`
        : `${baseURL}/sintomas/${id}`;
        
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.mensagem || 'Erro ao excluir sintoma');
      }
      
      return await response.json();
    } catch (error) {
      console.error('[SintomasService] Erro ao excluir sintoma:', error);
      throw error;
    }
  }
}

export const sintomasService = new SintomasService();
